import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "./supabaseClient";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import HowItWorks from "./components/HowItWorks";

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Paris";

// --- Affichage du quota & info période (Royal)
function QuotaDisplay({ userId }) {
  const [abo, setAbo] = useState(null);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      const { data, error } = await supabase
        .from("abonnements")
        .select("formule, quota_max, quota_utilise, abonnement_actif, date_fin, date_debut")
        .eq("user_id", userId)
        .maybeSingle();
      if (!error) setAbo(data || null);
    })();
  }, [userId]);

  if (!abo) return <div>Chargement…</div>;

  const max = abo.quota_max ?? 3000;
  const used = abo.quota_utilise ?? 0;
  const percent = Math.min(100, Math.round((used / Math.max(max, 1)) * 100));

  // date_fin (UTC) -> fuseau local
  const nowLocal = dayjs().tz(TZ);
  const finLocal = abo.date_fin ? dayjs.utc(abo.date_fin).tz(TZ) : null;

  // --- Compensation changement d’heure (only fallback -> +1h)
  // Si l’offset à la fin < offset maintenant (été -> hiver), on ajoute +1h en affichage/compte à rebours.
  let finAffichee = finLocal;
  if (finLocal) {
    const offsetNow = nowLocal.utcOffset(); // minutes
    const offsetFin = finLocal.utcOffset(); // minutes
    if (offsetFin < offsetNow) {
      finAffichee = finLocal.add(1, "hour");
    }
  }

  const expired = finAffichee ? nowLocal.isAfter(finAffichee) : false;

  // Temps restant (jours + heures) basé sur la date affichée (éventuellement +1h)
  let restantTxt = "";
  if (finAffichee && !expired) {
    const totalHours = finAffichee.diff(nowLocal, "hour");
    const jours = Math.floor(totalHours / 24);
    const heures = totalHours % 24;
    restantTxt = `${jours} jour${jours > 1 ? "s" : ""} ${heures}h restantes`;
  }

  const quotaAtteint = used >= max;

  return (
    <div className="text-center">
      <div className="mb-1">
        <b>Plan :</b> {(abo.formule || "royal").toString().toUpperCase()} &nbsp;•&nbsp;
        <b>Quota :</b> {used} / {max}
      </div>

      <div className="w-full h-2 bg-gray-200 rounded mt-1 mb-2 overflow-hidden">
        <div className="h-2 bg-blue-500" style={{ width: `${percent}%` }} />
      </div>

      {expired ? (
        <div className="text-red-600 font-semibold">❌ Abonnement expiré</div>
      ) : quotaAtteint ? (
        <div className="text-orange-600 font-semibold">
          Quota atteint. L’accès reprendra au renouvellement (tous les 14 jours).
        </div>
      ) : (
        <div className="text-green-700 font-semibold">✅ Abonnement actif</div>
      )}

      {finAffichee && (
        <div className="text-xs text-gray-500 mt-1">
          Fin de période&nbsp;: {finAffichee.format("DD/MM/YYYY HH:mm")}
          {restantTxt && <span> — {restantTxt}</span>}
        </div>
      )}

      {/* Note interne : la compensation DST est purement visuelle (ne change pas la date serveur). */}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const getUserSession = async () => {
      const { data } = await supabase.auth.getSession();
      setUser(data?.session?.user || null);
    };
    getUserSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => {
      authListener?.subscription?.unsubscribe?.();
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <button
        onClick={handleLogout}
        className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-semibold absolute top-6 right-8"
      >
        Déconnexion
      </button>

      <div className="flex-1 max-w-6xl mx-auto w-full px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <HowItWorks />
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white p-10 rounded-xl shadow-lg max-w-2xl w-full mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Bienvenue sur <span className="text-blue-600">V-repop</span> !
              </h1>

              <div className="mb-8 text-center">
                {user ? (
                  <>
                    <QuotaDisplay userId={user.id} />
                    <div className="text-xs text-gray-400 mt-1">
                      Connecté en tant que <b>{user.email}</b>
                    </div>
                  </>
                ) : (
                  <span className="text-gray-500">Chargement du statut d’abonnement…</span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Link
                  to="/subscription"
                  className="w-full text-center bg-white border border-gray-200 rounded-xl px-4 py-3 font-medium hover:bg-gray-50 transition"
                >
                  Choisir mon offre
                </Link>

                <a
                  href="https://chromewebstore.google.com/detail/vrepop/depkoiepmnjkkbpcimmfhfndjjbbpkbl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center bg-white border border-gray-200 rounded-xl px-4 py-3 font-medium hover:bg-gray-50 transition"
                >
                  Télécharger l’extension
                </a>

                <Link
                  to="/settings"
                  className="w-full text-center bg-white border border-gray-200 rounded-xl px-4 py-3 font-medium hover:bg-gray-50 transition"
                >
                  Paramètres
                </Link>

                {/* ✅ Remplacement du tuto unique par 2 installs */}
                <Link
                  to="/install-pc"
                  className="w-full text-center bg-white border border-gray-200 rounded-xl px-4 py-3 font-medium hover:bg-gray-50 transition"
                >
                  🖥️ Installer sur PC
                </Link>

                <Link
                  to="/install-android"
                  className="w-full text-center bg-white border border-gray-200 rounded-xl px-4 py-3 font-medium hover:bg-gray-50 transition"
                >
                  📱 Installer sur Android
                </Link>

                <a
                  href="/faq.html"
                  className="w-full text-center bg-white border border-gray-200 rounded-xl px-4 py-3 font-medium hover:bg-gray-50 transition"
                >
                  ❓ FAQ
                </a>
              </div>

              <p className="text-xs text-center mt-8 text-gray-500">
                En utilisant V-repop, vous acceptez pleinement nos{" "}
                <a href="/cgv" target="_blank" rel="noopener noreferrer" className="underline">
                  Conditions Générales de Vente (CGV)
                </a>{" "}
                et{" "}
                <a href="/cgu" target="_blank" rel="noopener noreferrer" className="underline">
                  Conditions Générales d’Utilisation (CGU)
                </a>.
              </p>

              <div className="mt-4 text-xs text-gray-500 text-center">
                Plan unique <b>ROYAL</b> — 2 € / 14 jours — 3 000 republications par période. <br />
                Fin d’accès au premier des deux : quota atteint ou expiration des 14 jours. Pas de report.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}